import {
    cancelSelectEvent,
    changeSymbolForWidget,
    getCSSStyles,
    getHeaderPinButtonHTML,
    getHeaderPinStateClass,
    getSymbolDropdown,
    getTraderSMCardHTML,
    getWidgetDataURI,
    normalizeWidgetTheme,
    setupHeaderPin,
    toggleWidgetTheme
} from '../../helpers';
import { WidgetConfig, WidgetEvent, WidgetEventHandler, WidgetEventType } from '../../types';

interface EsgBadgeData {
    symbol: string;
    score: number;
    performanceLabel: string;
    categoryLabel: string;
    badgeColor: string;
}

const BADGE_COLOR = '#6000FF';
const CATEGORY_LABEL = 'ESG Score';

const DUMMY_ESG_BADGE_SCORES: Record<string, { score: number; performanceLabel?: string }> = {
    AAPL: { score: 73.7, performanceLabel: 'Strong ESG Performance' },
    MSFT: { score: 82.4 },
    GOOGL: { score: 68.2 },
    AMZN: { score: 55.7 },
    NVDA: { score: 74.8 },
    META: { score: 63.9 },
    TSLA: { score: 78.6 },
    AVGO: { score: 44.8 },
    PEP: { score: 88.3 },
    COST: { score: 71.5 }
};

function formatScore(value: number): string {
    return Number.isInteger(value) ? String(value) : value.toFixed(1);
}

function getPerformanceLabel(score: number, override?: string): string {
    if (override) return override;
    if (score >= 85) return 'Excellent ESG Performance';
    if (score >= 70) return 'Strong ESG Performance';
    if (score >= 50) return 'Moderate ESG Performance';
    if (score >= 30) return 'Weak ESG Performance';
    return 'Poor ESG Performance';
}

function buildBadgeData(symbol: string): EsgBadgeData {
    const entry = DUMMY_ESG_BADGE_SCORES[symbol] ?? DUMMY_ESG_BADGE_SCORES.AAPL;
    return {
        symbol,
        score: entry.score,
        performanceLabel: getPerformanceLabel(entry.score, entry.performanceLabel),
        categoryLabel: CATEGORY_LABEL,
        badgeColor: BADGE_COLOR
    };
}

export class TraderverseESGCompanyPerformanceBadge {
    private container: HTMLElement;
    private config: WidgetConfig;
    private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
    private shadowRoot: ShadowRoot;
    private badgeData: EsgBadgeData | null = null;
    private widgetResizeObserver: ResizeObserver | null = null;

    constructor(config: WidgetConfig) {
        this.config = { ...this.getDefaultConfig(), ...config };
        this.config.theme = normalizeWidgetTheme(this.config.theme);
        this.container = this.resolveContainer(config.container);
        this.shadowRoot = (this.container as any).shadowRoot || this.container.attachShadow({ mode: 'closed' });
        this.init();
        this.widgetResizeObserver = new ResizeObserver(() => this.updateDisplay());
        setTimeout(() => {
            const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
            if (widget) this.widgetResizeObserver?.observe(widget);
        }, 0);
    }

    private fetchSymbols(): Array<{ value: string; label: string }> {
        return Object.keys(DUMMY_ESG_BADGE_SCORES).map((symbol) => ({ label: symbol, value: symbol }));
    }

    private getDefaultConfig(): Partial<WidgetConfig> {
        const symbols = this.fetchSymbols();
        return {
            headerPinned: true,
            symbol: symbols[0].value,
            theme: 'default-dark',
            width: '100%',
            height: '400px',
            autosize: true,
            isSymbolSelectable: true
        };
    }

    private resolveContainer(container: string | HTMLElement): HTMLElement {
        if (typeof container === 'string') {
            const el = document.getElementById(container) || document.querySelector(container);
            if (!el) throw new Error(`Container not found: ${container}`);
            return el as HTMLElement;
        }
        return container;
    }

    private async init(): Promise<void> {
        if (!this.config.symbol) this.config.symbol = this.fetchSymbols()[0].value;
        this.createWidget();
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        if (widget) widget.classList.add('loading');
        await this.attachStyles();
        this.loadDummyData(this.config.symbol!);
        if (widget) widget.classList.remove('loading');
        cancelSelectEvent(this.shadowRoot);
        this.emit('ready', {});
        this.config.onReady?.();
    }

    private createWidget(): void {
        let themeClass = 'defaultDarkMode';
        let dataTheme = 'default-dark';

        if (this.config.theme === 'white') {
            themeClass = 'whiteMode';
            dataTheme = 'white';
        } else if (this.config.theme === 'black') {
            themeClass = 'blackMode';
            dataTheme = 'black';
        } else if (this.config.theme === 'default-lite') {
            themeClass = 'defaultLightMode';
            dataTheme = 'default-lite';
        }

        const widgetHtml = `
        <div style="width: ${typeof this.config.width == 'number' ? this.config.width + 'px' : this.config.width};height: ${typeof this.config.height == 'number' ? this.config.height + 'px' : this.config.height};" class="widgetBox ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-theme="${dataTheme}">
            <div class="headTop">
                <h4 class="title market">${this.getTitleIconSvg()} ESG Company Performance Badge</h4>
                <div class="actionCol">${getHeaderPinButtonHTML(this.config)}<i class="pi pi-ellipsis-v"></i></div>
            </div>
            <div class="inBody">
                <div class="optionsRow">
                    ${this.config.hideSymbol ? '<div class="item dropStyle01"></div>' : `<div class="item dropStyle01">
                        <div class="trader-sm-card" id="selected-symbol">
                            ${getTraderSMCardHTML(this.config.stock ?? { symbol: this.config.symbol!, type: 'stock', name: '' })}
                        </div>
                        <span id="symbol-dropdown-placeholder"></span>
                    </div>`}
                </div>
                <div class="chartDv esgPerformanceBadgeMain" id="esgPerformanceBadgeContainer">
                    <div id="esgPerformanceBadge" class="chart" data-testing-id="esg-performance-badge"></div>
                </div>
            </div>
        </div>`;
        this.shadowRoot.innerHTML = widgetHtml;
        setupHeaderPin(this.shadowRoot, this.config);

        if (this.config.isSymbolSelectable && !this.config.hideSymbol) {
            const dropdown = getSymbolDropdown((data: any) => this.onSelectChange(data));
            const placeholder = this.shadowRoot.getElementById('symbol-dropdown-placeholder');
            if (placeholder && dropdown) placeholder.replaceWith(dropdown);
        }
    }

    private onSelectChange(stock: any): void {
        this.changeSymbol(stock.symbol, stock);
    }

    private async attachStyles(): Promise<void> {
        try {
            const styleEl = await getCSSStyles();
            this.shadowRoot.appendChild(styleEl);
            const badgeStyle = document.createElement('style');
            badgeStyle.textContent = `
                .esgPerformanceBadgeMain {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    overflow: hidden;
                }
                .esgPerformanceBadgeMain .coverDv,
                .esgPerformanceBadgeMain .chart {
                    position: relative !important;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    height: 100% !important;
                    width: 100% !important;
                }
                .esg-performance-badge {
                    container-type: size;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 100%;
                    height: 100%;
                    padding: 20px;
                    box-sizing: border-box;
                }
                .esg-badge-circle {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                    aspect-ratio: 1;
                    height: min(100%, 320px);
                    width: auto;
                    max-width: min(72%, 320px);
                    border-radius: 50%;
                    text-align: center;
                    padding: 24px;
                    box-sizing: border-box;
                }
                @supports (width: 1cqw) {
                    .esg-badge-circle {
                        height: auto;
                        width: min(320px, 72cqw, 100cqh);
                        max-width: none;
                    }
                }
                .esg-badge-score {
                    color: #ffffff;
                    font-size: clamp(36px, 8vw, 56px);
                    font-weight: 700;
                    line-height: 1.1;
                    margin: 0 0 8px;
                }
                .esg-badge-performance {
                    color: #ffffff;
                    font-size: clamp(14px, 2.5vw, 18px);
                    font-weight: 700;
                    line-height: 1.3;
                    margin: 0 0 6px;
                    max-width: 90%;
                }
                .esg-badge-category {
                    color: rgba(255, 255, 255, 0.72);
                    font-size: clamp(12px, 2vw, 14px);
                    font-weight: 400;
                    line-height: 1.2;
                    margin: 0;
                }
            `;
            this.shadowRoot.appendChild(badgeStyle);
        } catch (err) {
            console.error('Failed to load CSS file:', err);
        }
    }

    private loadDummyData(symbol: string): void {
        this.badgeData = buildBadgeData(symbol);
        this.renderBadge();
        this.emit('dataUpdate', this.badgeData);
    }

    private updateDisplay(): void {
        if (!this.badgeData) return;
        this.renderBadge();
    }

    private renderBadge(): void {
        const container = this.shadowRoot.querySelector('#esgPerformanceBadge') as HTMLElement;
        if (!container || !this.badgeData) return;

        container.innerHTML = `
            <div class="esg-performance-badge">
                <div class="esg-badge-circle" style="background:${this.badgeData.badgeColor};">
                    <p class="esg-badge-score">${formatScore(this.badgeData.score)}</p>
                    <p class="esg-badge-performance">${this.badgeData.performanceLabel}</p>
                    <p class="esg-badge-category">${this.badgeData.categoryLabel}</p>
                </div>
            </div>`;
    }

    private changeSymbol(stockId: string, stock: any = null): void {
        changeSymbolForWidget(
            this.shadowRoot,
            this.config,
            stock,
            stockId,
            (symbol) => this.loadDummyData(symbol),
            (type, data) => this.emit(type, data)
        );
    }

    public on(type: WidgetEventType, handler: WidgetEventHandler): void {
        if (!this.eventHandlers.has(type)) this.eventHandlers.set(type, []);
        this.eventHandlers.get(type)!.push(handler);
    }

    public off(type: WidgetEventType, handler: WidgetEventHandler): void {
        const list = this.eventHandlers.get(type);
        if (list) this.eventHandlers.set(type, list.filter((h) => h !== handler));
    }

    public setSymbol(symbol: string): void {
        this.changeSymbol(symbol);
    }

    public async getWidgetImage(): Promise<string> {
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        return await getWidgetDataURI(widget);
    }

    public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite'): void {
        if (!this.shadowRoot) return;
        this.config.theme = toggleWidgetTheme(this.shadowRoot, theme) as 'white' | 'black' | 'default-dark' | 'default-lite';
        this.updateDisplay();
    }

    public destroy(): void {
        this.widgetResizeObserver?.disconnect();
        this.eventHandlers.clear();
        this.shadowRoot.innerHTML = '';
    }

    private emit(type: WidgetEventType, data: any): void {
        const handlers = this.eventHandlers.get(type);
        if (handlers) {
            const evt: WidgetEvent = { type, data };
            handlers.forEach((h) => h(evt));
        }
    }

    private getTitleIconSvg(): string {
        return `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M18.75 28.4375H11.25C4.4625 28.4375 1.5625 25.5375 1.5625 18.75V11.25C1.5625 4.4625 4.4625 1.5625 11.25 1.5625H18.75C25.5375 1.5625 28.4375 4.4625 28.4375 11.25V18.75C28.4375 25.5375 25.5375 28.4375 18.75 28.4375ZM11.25 3.4375C5.4875 3.4375 3.4375 5.4875 3.4375 11.25V18.75C3.4375 24.5125 5.4875 26.5625 11.25 26.5625H18.75C24.5125 26.5625 26.5625 24.5125 26.5625 18.75V11.25C26.5625 5.4875 24.5125 3.4375 18.75 3.4375H11.25Z" fill="currentColor"/> <path d="M9.16252 19.05C8.96252 19.05 8.76252 18.9875 8.58752 18.85C8.17502 18.5375 8.10002 17.95 8.41252 17.5375L11.3875 13.675C11.75 13.2125 12.2625 12.9125 12.85 12.8375C13.425 12.7625 14.0125 12.925 14.475 13.2875L16.7625 15.0875C16.85 15.1625 16.9375 15.1625 17 15.15C17.05 15.15 17.1375 15.125 17.2125 15.025L20.1 11.3C20.4125 10.8875 21.0125 10.8125 21.4125 11.1375C21.825 11.45 21.9 12.0375 21.575 12.45L18.6875 16.175C18.325 16.6375 17.8125 16.9375 17.225 17C16.6375 17.075 16.0625 16.9125 15.6 16.55L13.3125 14.75C13.225 14.675 13.125 14.675 13.075 14.6875C13.025 14.6875 12.9375 14.7125 12.8625 14.8125L9.88752 18.675C9.72502 18.925 9.45002 19.05 9.16252 19.05Z" fill="currentColor"/> </svg>`;
    }
}

declare global {
    interface Window {
        TraderverseESGCompanyPerformanceBadge: typeof TraderverseESGCompanyPerformanceBadge;
    }
}
if (typeof window !== 'undefined') {
    window.TraderverseESGCompanyPerformanceBadge = TraderverseESGCompanyPerformanceBadge;
}
